import { isEqual } from 'lodash';
import * as objectPath from 'object-path';

import { ColumnState } from '@ag-grid-community/core';
import {
  FieldType,
  FilterOperator,
  FilterType,
  FilterValue,
} from '@ideapa/common-interfaces';
import { sortNumber } from '@ideapa/common-utils';

import { AgGridFilter, AgGridFilterModel } from './aggrid-models';
import { EntityListField } from './entity-list-field';

export function mapToFilterModel(filters: FilterValue[]) {
  const filterModel: AgGridFilterModel = {};
  return filters.reduce((res, f) => {
    if (f.type !== FilterType.date && f.type !== FilterType.datetime) {
      res[f.field] = buildAgGridFilter(f);
    } else {
      res[f.field] = buildAgGridDateFilter(f);
    }

    return res;
  }, filterModel);
}

export function getAgGridFilterType(filterType: FilterType) {
  switch (filterType) {
    case FilterType.date:
    case FilterType.datetime:
      return 'date';
    case FilterType.number:
      return 'number';
    case FilterType.boolean:
      return 'set';
    default:
      return 'text';
  }
}

export function getDefaultOperator(type: FilterType) {
  if (type === FilterType.text) {
    return FilterOperator.contains;
  } else if (type === FilterType.date || type === FilterType.datetime) {
    return FilterOperator.equals;
  } else if (type === FilterType.number) {
    return FilterOperator.equals;
  } else if (type === FilterType.boolean) {
    return FilterOperator.equals;
  } else if (type === FilterType.enum) {
    return FilterOperator.equals;
  } else if (type === FilterType.autocomplete) {
    return FilterOperator.equals;
  }

  return FilterOperator.contains;
}

export function buildAgGridFilter(f: {
  type: FilterType;
  operator: FilterOperator;
  value: any[];
}): AgGridFilter {
  const obj = {
    type: FilterOperator[f.operator],
    filterType: getAgGridFilterType(f.type),
    filter: f.value.length ? f.value[0] : undefined,
    filterTo: f.value.length === 2 ? f.value[1] : undefined,
  } as AgGridFilter;

  if (obj.filterTo == null) {
    delete obj.filterTo;
  }
  return obj;
}
export function buildAgGridDateFilter(f: {
  type: FilterType;
  operator: FilterOperator;
  value: any[];
}): AgGridFilter {
  const obj = {
    type: FilterOperator[f.operator],
    filterType: getAgGridFilterType(f.type),
    dateFrom: f.value.length ? f.value[0] : undefined,
    dateTo: f.value.length === 2 ? f.value[1] : undefined,
  } as AgGridFilter;

  if (obj.dateTo == null) {
    obj.dateTo = null;
  }
  return obj;
}

export function mapFromFilterModel(
  filterModel?: AgGridFilterModel,
  fields?: EntityListField[]
) {
  if (!filterModel) {
    return [];
  }
  const filters: FilterValue[] = [];
  Object.keys(filterModel).forEach((key) => {
    const gridFilter = filterModel[key];
    const f = {
      field: key,
      operator: (<any>FilterOperator)[gridFilter.type],
      type:
        fields?.find((f) => f.field === key)?.filterType ??
        (<any>FilterType)[gridFilter.filterType],
      value: [],
    } as FilterValue;

    if (f.type !== FilterType.date && f.type !== FilterType.datetime) {
      if (gridFilter.filter != null) {
        f.value.push(gridFilter.filter);
      }

      if (gridFilter.filterTo != null) {
        f.value.push(gridFilter.filterTo);
      }
    } else {
      if (gridFilter.dateFrom != null) {
        f.value.push(gridFilter.dateFrom);
      }

      if (gridFilter.dateTo != null) {
        f.value.push(gridFilter.dateTo);
      }
    }

    filters.push(f);
  });
  return filters;
}

export function mapFromColumnState(columnState?: ColumnState[]): {
  [field: string]: 'asc' | 'desc';
} {
  if (!columnState) return {};
  return columnState
    .filter((cs) => !!cs.sort && !!cs.colId)
    .sort((cs1, cs2) => sortNumber(cs1, cs2, 'sortIndex'))
    .reduce((acc: { [key: string]: any }, item) => {
      const colId = item.colId as string;
      const sort = item.sort as string;
      return { ...acc, [colId]: sort.toUpperCase() };
    }, {});
}

export function equalFilters(
  gridFilterModel: AgGridFilterModel | undefined,
  filters: FilterValue[]
) {
  const filtersMapped = mapToFilterModel(filters);

  if (!isEqual(filtersMapped, gridFilterModel)) {
    return false;
  }
  return true;
}

export function getGridColumnType(type: FilterType, fieldType?: FieldType) {
  switch (type) {
    case FilterType.datetime:
      return 'datetimeColumn';
    case FilterType.date:
      return 'dateColumn';
    case FilterType.number:
      if (fieldType === FieldType.currency) return 'currencyColumn';
      if (fieldType === FieldType.percent) return 'percentColumn';
      return 'numberColumn';
    case FilterType.boolean:
      return 'boolColumn';
    case FilterType.enum:
      return 'enumColumn';
    default:
      return ''; // text dà errore in console
  }
}

export function getEventPath(event: Event): HTMLElement[] {
  const path: HTMLElement[] = [];
  let currentTarget: any = event.target;
  while (currentTarget) {
    path.push(currentTarget);
    currentTarget = currentTarget.parentElement;
  }
  return path;
}
export function getAllFocusableElementsOf(el: HTMLElement) {
  return Array.from<HTMLElement>(
    el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((focusableEl) => {
    return focusableEl.tabIndex !== -1;
  });
}
export function filterByAgGridFilterModel(
  data: any[],
  agGridFilters: AgGridFilterModel | undefined,
  fields?: EntityListField[]
) {
  const filters: FilterValue[] = mapFromFilterModel(agGridFilters, fields);
  const filteredData = filters
    ? data.filter((item: any) => {
        let res = true;
        for (const filter of filters) {
          const value = objectPath.get(item, filter.field);
          if (
            filter.type === FilterType.boolean &&
            filter.value.length === 1 &&
            (filter.value[0] === 'true' || filter.value[0] === 'false')
          ) {
            filter.value[0] = filter.value[0] === 'true' ? true : false;
          }
          switch (filter.operator) {
            case FilterOperator.contains:
              res &&= value
                .toLowerCase()
                .includes(filter.value[0].toLowerCase());
              break;
            case FilterOperator.notContains:
              res &&=
                !value ||
                !value.toLowerCase().includes(filter.value[0].toLowerCase());
              break;
            case FilterOperator.equals:
              res &&= value.toLowerCase() === filter.value[0].toLowerCase();
              break;
            case FilterOperator.notEqual:
              res =
                !value || value.toLowerCase() !== filter.value[0].toLowerCase();
              break;
            case FilterOperator.startsWith:
              res &&= value
                .toLowerCase()
                .startsWith(filter.value[0].toLowerCase());
              break;
            case FilterOperator.endsWith:
              res &&= value
                .toLowerCase()
                .endsWith(filter.value[0].toLowerCase());
              break;
            case FilterOperator.greaterThan:
              res = !value || value > filter.value[0];
              break;
            case FilterOperator.lessThan:
              res = !value || value < filter.value[0];
              break;
            case FilterOperator.greaterThanOrEqual:
              res = !value || value >= filter.value[0];
              break;
            case FilterOperator.lessThanOrEqual:
              res = !value || value <= filter.value[0];
              break;
            case FilterOperator.inRange:
              res = value > filter.value[0] && value < filter.value[1];
              break;
            case FilterOperator.blank:
              res =
                !value ||
                (filter.type === FilterType.text ? value === '' : false);
              break;
            case FilterOperator.notBlank:
              res = !(
                !value ||
                (filter.type === FilterType.text ? value === '' : false)
              );
              break;
          }
        }
        return res;
      })
    : data;
  return filteredData;
}
